import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  User,
  Mail,
  Phone,
  Calendar,
  Target,
  Droplets,
  Trophy,
  Star,
  Edit3,
  Save,
  Camera,
  Award,
  TrendingUp,
  Zap,
  Bell,
  Volume2,
  AlertCircle,
  CheckCircle2,
  Building2,
  Lock,
  ChevronRight,
  LogOut,
  FileText,
  Trash2,
} from 'lucide-react';
import Navigation from '@/components/navigation';
import BrandHeader from '@/components/BrandHeader';
import { AVATAR_OPTIONS } from '@/components/avatar-selection';
import { apiRequest } from '@/lib/queryClient';
import { getErrorMessage } from '@/lib/errors';
import { getDisplayName, getDisplayInitials, getProgramDayCount } from '@/lib/displayName';
import { notificationService } from '@/services/notificationService';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  resolveGoalLitres,
  resolveFrequencyGoal,
  resolveVolumePerReminder,
} from '@/lib/hydrationGoals';

export default function Profile() {
  const [, setLocation] = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    age: 0,
    gender: '',
    dailyTarget: 0,
    reminderInterval: 0,
    activeHoursStart: '',
    activeHoursEnd: '',
    activeDays: [] as string[],
    pushNotifications: true,
    waterReminders: true,
    achievementNotifications: true,
    soundEnabled: true,
  });
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>('default');
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  // Fetch user profile data
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['/api/profile'],
    enabled: isAuthenticated,
    retry: false,
  });

  // Fetch notification preferences
  const { data: notificationPreferences, isLoading: preferencesLoading } = useQuery({
    queryKey: ['/api/notifications/preferences', (user as any)?.id],
    queryFn: async () => {
      const userId = (user as any)?.id;
      if (!userId) {
        throw new Error('User ID not available');
      }
      const response = await apiRequest('GET', `/api/notifications/preferences/${userId}`);
      return response.json();
    },
    enabled: !!(user as any)?.id && isAuthenticated,
    retry: false,
  });

  // Check if profile is complete (has wellness goals configured)
  const isProfileComplete =
    profileData && (profileData as any)?.dailyTarget && (profileData as any)?.reminderInterval;

  // Initialize edit data when profile loads
  useEffect(() => {
    if (profileData) {
      const profile = profileData as any;
      setEditData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        phoneNumber: profile.phoneNumber || '',
        age: profile.age || 0,
        gender: profile.gender || '',
        dailyTarget: profile.dailyTarget || 2000,
        reminderInterval: profile.reminderInterval || 45,
        activeHoursStart: profile.activeHoursStart || '09:00',
        activeHoursEnd: profile.activeHoursEnd || '18:00',
        activeDays: profile.activeDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        pushNotifications: true,
        waterReminders: true,
        achievementNotifications: true,
        soundEnabled: true,
      });
    }
  }, [profileData]);

  // Load notification preferences
  useEffect(() => {
    if (notificationPreferences) {
      const prefs = notificationPreferences as any;
      setEditData((prev) => ({
        ...prev,
        pushNotifications: prefs.pushNotifications ?? true,
        waterReminders: prefs.waterReminders ?? true,
        achievementNotifications: prefs.achievementNotifications ?? true,
        soundEnabled: prefs.soundEnabled ?? true,
      }));
    }
  }, [notificationPreferences]);

  // Notifications stay always on — no mute toggle (PRODUCT_CONTRACT.md)
  useEffect(() => {
    const updatePermissionStatus = () => {
      const status = notificationService.getPermissionStatus();
      setNotificationPermission(status);
    };

    updatePermissionStatus();
    notificationService.addPermissionChangeListener(updatePermissionStatus);

    return () => {
      notificationService.removePermissionChangeListener(updatePermissionStatus);
    };
  }, []);

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: any) => {
      // Update profile data
      const profileResponse = await apiRequest('PUT', '/api/profile', {
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
        age: data.age,
        gender: data.gender,
        dailyTarget: data.dailyTarget,
        reminderInterval: data.reminderInterval,
        activeHoursStart: data.activeHoursStart,
        activeHoursEnd: data.activeHoursEnd,
        activeDays: data.activeDays,
      });

      // Update notification preferences
      const userId = (user as any)?.id;
      if (userId) {
        await apiRequest('PUT', `/api/notifications/preferences/${userId}`, {
          pushNotifications: true,
          waterReminders: true,
          achievementNotifications: data.achievementNotifications,
          soundEnabled: data.soundEnabled,
          reminderInterval: data.reminderInterval,
          activeHoursStart: data.activeHoursStart,
          activeHoursEnd: data.activeHoursEnd,
        });
      }

      return profileResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/profile'] });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      queryClient.invalidateQueries({ queryKey: ['/api/user/stats'] });
      queryClient.invalidateQueries({ queryKey: ['/api/stats/daily'] });
      queryClient.invalidateQueries({
        queryKey: ['/api/notifications/preferences', (user as any)?.id],
      });
      toast({
        title: 'Profile Updated',
        description: 'Your profile and notification settings have been successfully updated.',
      });
      setIsEditing(false);
    },
    onError: (error: any) => {
      console.error('Profile update error:', error);
      toast({
        variant: 'destructive',
        title: 'Update Failed',
        description: getErrorMessage(error),
      });
    },
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset to original data
    if (profileData) {
      const profile = profileData as any;
      const prefs = notificationPreferences as any;
      setEditData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        phoneNumber: profile.phoneNumber || '',
        age: profile.age || 0,
        gender: profile.gender || '',
        dailyTarget: profile.dailyTarget || 2000,
        reminderInterval: profile.reminderInterval || 45,
        activeHoursStart: profile.activeHoursStart || '09:00',
        activeHoursEnd: profile.activeHoursEnd || '18:00',
        activeDays: profile.activeDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        pushNotifications: prefs?.pushNotifications ?? true,
        waterReminders: prefs?.waterReminders ?? true,
        achievementNotifications: prefs?.achievementNotifications ?? true,
        soundEnabled: prefs?.soundEnabled ?? true,
      });
    }
  };

  const handleSave = () => {
    // Validate required fields
    if (!editData.firstName || !editData.lastName || !editData.email) {
      toast({
        variant: 'destructive',
        title: 'Validation Error',
        description: 'Please fill in all required fields (First Name, Last Name, Email).',
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editData.email)) {
      toast({
        variant: 'destructive',
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
      });
      return;
    }

    // Validate age
    if (editData.age < 13 || editData.age > 120) {
      toast({
        variant: 'destructive',
        title: 'Invalid Age',
        description: 'Age must be between 13 and 120 years.',
      });
      return;
    }

    // Validate daily target
    if (editData.dailyTarget < 500 || editData.dailyTarget > 10000) {
      toast({
        variant: 'destructive',
        title: 'Invalid Target',
        description: 'Daily target must be between 500ml and 10000ml.',
      });
      return;
    }

    console.log('Saving profile data:', editData);
    updateProfileMutation.mutate(editData);
  };

  const handleInputChange = (field: string, value: any) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEnableNotifications = async () => {
    try {
      const granted = await notificationService.requestPermission();
      if (granted) {
        toast({
          title: 'Notifications Enabled! 🎉',
          description: "You'll now receive water reminders on this device.",
        });
      } else {
        toast({
          title: 'Permission Denied',
          description: 'Please enable notifications in your browser settings to receive reminders.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to enable notifications. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleTestNotification = async () => {
    if (notificationPermission !== 'granted') {
      const granted = await notificationService.requestPermission();
      if (!granted) {
        toast({
          title: 'Permission Required',
          description: 'Please allow notifications in the popup to test them.',
          variant: 'destructive',
        });
        return;
      }
    }

    const success = notificationService.showTestNotification();
    if (success) {
      toast({
        title: 'Test Sent! 🧪',
        description: 'Check your notifications to see the test reminder.',
      });
    } else {
      toast({
        title: 'Test Failed',
        description: 'Could not send test notification. Please check your browser settings.',
        variant: 'destructive',
      });
    }
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await logout();
      setLocation('/');
    } catch {
      toast({
        variant: 'destructive',
        title: 'Sign out failed',
        description: 'Please try again.',
      });
    } finally {
      setIsSigningOut(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteAcknowledged) return;
    const confirmed = window.confirm(
      'This will permanently delete your profile and all hydration data. This cannot be undone.'
    );
    if (!confirmed) return;

    try {
      const response = await apiRequest('DELETE', '/api/profile');
      if (!response.ok) {
        throw new Error('Delete failed');
      }
      await logout();
      toast({
        title: 'Account deleted',
        description: 'Your profile and data have been removed.',
      });
      setLocation('/');
    } catch {
      toast({
        variant: 'destructive',
        title: 'Could not delete account',
        description: 'Please try again or contact support.',
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-hydro-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-lilac-300 border-t-purple-500 mx-auto mb-4"></div>
          <p className="text-deep-600 font-medium font-kumbh">Please log in to view your profile</p>
        </div>
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className="min-h-screen pb-24 bg-hydro-100">
        <div className="max-w-md mx-auto px-6 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-lilac-200 rounded w-1/4"></div>
            <div className="h-32 bg-lilac-200 rounded-xl"></div>
            <div className="h-24 bg-lilac-200 rounded-xl"></div>
            <div className="h-48 bg-lilac-200 rounded-xl"></div>
          </div>
        </div>
        <Navigation />
      </div>
    );
  }

  const userName = getDisplayName({
    ...(profileData as any),
    ...(user as any),
    avatarName: (profileData as any)?.avatarName || (user as any)?.profile?.avatarName,
  });
  const createdAtRaw =
    (profileData as any)?.profileCreatedAt ||
    (profileData as any)?.createdAt ||
    (user as any)?.createdAt;
  const dayCount = getProgramDayCount({
    profileCreatedAt: (profileData as any)?.profileCreatedAt,
    userCreatedAt: createdAtRaw,
  });

  const nickname = userName;
  const initials = getDisplayInitials({
    avatarName: (profileData as any)?.avatarName,
    firstName: (profileData as any)?.firstName,
    lastName: (profileData as any)?.lastName,
  });
  const profile = profileData as any;
  const dailyGoalL = resolveGoalLitres(profile);
  const avgVol = resolveVolumePerReminder(profile);
  const totalSips = resolveFrequencyGoal(profile);
  const reminderInterval = profile?.reminderInterval || 45;
  const avatarImageUrl = profile?.profilePictureUrl || profile?.profileImageUrl;
  const avatarDisplayUrl =
    AVATAR_OPTIONS.find((a) => a.id === profile?.avatarType)?.image || avatarImageUrl;
  const company = profile?.company;
  const department = profile?.department;

  return (
    <div className="min-h-screen pb-32 bg-cream">
      <div className="max-w-md mx-auto px-5 pt-6 space-y-4">
        <BrandHeader title="Profile" nickname={nickname} />

        <div className="bg-cream rounded-3xl p-6 border border-deep-100/50">
          <div className="flex flex-col items-center">
            <div className="relative">
              <button
                type="button"
                onClick={() => setLocation('/edit-profile')}
                className="relative w-20 h-20 flex items-center justify-center"
              >
                <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
                  <path
                    d="M50 2 C58 2 62 8 70 10 C78 12 86 8 90 16 C94 24 88 30 90 38 C92 46 98 52 96 60 C94 68 86 70 82 78 C78 86 80 94 72 96 C64 98 58 92 50 92 C42 92 36 98 28 96 C20 94 22 86 18 78 C14 70 6 68 4 60 C2 52 8 46 10 38 C12 30 6 24 10 16 C14 8 22 12 30 10 C38 8 42 2 50 2 Z"
                    fill="hsl(var(--lilac-400))"
                    stroke="hsl(var(--deep-800))"
                    strokeWidth="2"
                  />
                </svg>
                <span className="relative text-purple-700 font-extrabold text-xl overflow-hidden rounded-full w-16 h-16 flex items-center justify-center">
                  {avatarDisplayUrl ? (
                    <img
                      src={avatarDisplayUrl}
                      alt={nickname}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    initials
                  )}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setLocation('/edit-profile')}
                className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-lime-500 border-2 border-cream flex items-center justify-center"
              >
                <Camera className="w-3.5 h-3.5 text-deep-800" />
              </button>
            </div>

            <p className="mt-4 text-base font-semibold text-deep-800">
              <span className="italic font-extrabold tracking-tighter">SWYG</span> X {nickname}
            </p>
            <p className="text-xs text-deep-500 mt-0.5">Day: {dayCount}</p>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-3 mt-6">
            <button
              type="button"
              onClick={() => setLocation('/edit-profile')}
              className="bg-purple-600 text-white font-semibold rounded-full px-8 py-3 shadow-[0_4px_0_hsl(var(--deep-800))] active:translate-y-0.5 active:shadow-[0_2px_0_hsl(var(--deep-800))] transition-all"
            >
              Edit Profile
            </button>
            <button
              type="button"
              onClick={() => setLocation('/hydration-settings')}
              className="border-2 border-purple-600 text-purple-700 font-semibold rounded-full px-8 py-3 active:translate-y-0.5 transition-all"
            >
              Hydration Settings
            </button>
          </div>

          <div className="mt-8">
            <div className="relative grid grid-cols-2 gap-y-2 gap-x-2">
              <ProfileBlob rotate={-3}>
                <p className="text-2xl font-extrabold text-deep-800">{dailyGoalL.toFixed(1)}L</p>
                <p className="text-[11px] text-deep-500 tracking-wide font-semibold mt-0.5">
                  DAILY VOLUME
                </p>
              </ProfileBlob>
              <ProfileBlob rotate={4}>
                <p className="text-2xl font-extrabold text-deep-800">{avgVol} ML</p>
                <p className="text-[11px] text-deep-500 tracking-wide font-semibold mt-0.5">
                  AVG VOL
                </p>
              </ProfileBlob>
              <ProfileBlob rotate={5}>
                <p className="text-2xl font-extrabold text-deep-800">{reminderInterval} min</p>
                <p className="text-[11px] text-deep-500 tracking-wide font-semibold mt-0.5">
                  REMIND EVERY
                </p>
              </ProfileBlob>
              <ProfileBlob rotate={-4}>
                <p className="text-2xl font-extrabold text-deep-800">{totalSips}</p>
                <p className="text-[11px] text-deep-500 tracking-wide font-semibold mt-0.5">
                  TOTAL SIPS
                </p>
              </ProfileBlob>
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 text-center pointer-events-none bg-cream/90 rounded-xl px-3 py-2">
                <p className="font-extrabold text-deep-800 text-sm leading-tight">
                  Current
                  <br />
                  Goals
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setLocation('/unlock-report')}
            className="w-full mt-6 bg-lime-500 text-deep-800 font-semibold rounded-full px-8 py-3.5 shadow-[0_4px_0_hsl(var(--deep-800))] active:translate-y-0.5 active:shadow-[0_2px_0_hsl(var(--deep-800))] transition-all flex items-center justify-center gap-2"
          >
            <Lock className="w-4 h-4" />
            30-Day Unlock Report
          </button>
        </div>

        {(company || department) && (
          <div className="bg-cream rounded-3xl p-5 border border-deep-100/50">
            <h3 className="font-bold text-deep-800 mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Company
            </h3>
            {company?.name && (
              <div className="flex justify-between py-2 gap-3">
                <span className="text-sm text-deep-500">Organization</span>
                <span
                  className="text-sm text-deep-800 font-medium text-right"
                  data-testid="profile-organization"
                >
                  {company.name}
                </span>
              </div>
            )}
            {department && (
              <div className="flex justify-between py-2">
                <span className="text-sm text-deep-500">Department</span>
                <span className="text-sm text-deep-800 font-medium">{department}</span>
              </div>
            )}
          </div>
        )}

        <div className="bg-cream rounded-3xl p-5 border border-deep-100/50">
          <h3 className="font-bold text-deep-800 mb-3">Settings</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3 py-2">
              <Bell className="w-4 h-4 text-deep-500 mt-0.5 shrink-0" />
              <div>
                <span className="text-sm text-deep-700">Notifications</span>
                <p className="text-xs text-deep-400" data-testid="notif-always-on">
                  {notificationPermission === 'denied'
                    ? 'Notifications stay always on, but this browser has blocked them. Enable SWYG in your browser or OS settings.'
                    : 'Notifications stay always on. Allow SWYG in your browser or OS if you are prompted.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-cream rounded-3xl p-5 border border-deep-100/50">
          <h3 className="font-bold text-deep-800 mb-3">Legal & account</h3>
          <div className="space-y-1">
            <a
              href="/legal/index.html"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between py-3"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-deep-500" />
                <span className="text-sm text-deep-700">User Policy</span>
              </div>
              <div className="flex items-center text-purple-600 text-sm font-medium">
                View <ChevronRight className="w-4 h-4" />
              </div>
            </a>
            {(user as any)?.termsAcceptedAt || (user as any)?.privacyAcceptedAt ? (
              <div className="rounded-2xl bg-white/70 border border-deep-100 px-3 py-2.5 mb-2">
                <p className="text-xs font-semibold text-deep-700 mb-1">Acceptance on file</p>
                <p className="text-[11px] text-deep-500 break-all">
                  User ID: {(user as any)?.id || '—'}
                </p>
                <p className="text-[11px] text-deep-500">
                  Terms {(user as any)?.termsVersion || '—'} ·{' '}
                  {(user as any)?.termsAcceptedAt
                    ? new Date((user as any).termsAcceptedAt).toLocaleString()
                    : '—'}
                </p>
                <p className="text-[11px] text-deep-500">
                  Privacy {(user as any)?.privacyVersion || '—'} ·{' '}
                  {(user as any)?.privacyAcceptedAt
                    ? new Date((user as any).privacyAcceptedAt).toLocaleString()
                    : '—'}
                </p>
              </div>
            ) : null}

            <div className="border-t border-deep-100 pt-3">
              <div className="flex items-center justify-between gap-3 py-2">
                <div className="flex items-center gap-3 min-w-0">
                  <Trash2 className="w-4 h-4 text-deep-500 shrink-0" />
                  <span className="text-sm text-deep-700">Delete profile and data</span>
                </div>
                <Checkbox
                  id="delete-ack"
                  checked={deleteAcknowledged}
                  onCheckedChange={(checked) => setDeleteAcknowledged(checked === true)}
                  className="h-5 w-5 rounded-md border-deep-300 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                />
              </div>
              {deleteAcknowledged && (
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  className="w-full mt-2 text-sm font-semibold text-red-600 py-2 rounded-full border border-red-200 hover:bg-red-50 transition-colors"
                >
                  Permanently delete my account
                </button>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleSignOut}
          disabled={isSigningOut}
          className="w-full border-2 border-deep-800 text-deep-800 font-semibold rounded-full px-8 py-3.5 flex items-center justify-center gap-2 active:translate-y-0.5 transition-all disabled:opacity-60"
        >
          <LogOut className="w-4 h-4" />
          {isSigningOut ? 'Signing out...' : 'Sign Out'}
        </button>
      </div>

      <Navigation />
    </div>
  );
}

function ProfileBlob({ rotate = 0, children }: { rotate?: number; children: React.ReactNode }) {
  return (
    <div
      className="relative w-full h-32 flex items-center justify-center px-2"
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      <svg
        viewBox="0 0 200 160"
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="none"
      >
        <path
          d="M40,20 C70,5 130,0 165,25 C195,50 198,100 175,135 C150,170 80,165 45,145 C10,125 5,75 15,50 C20,35 28,28 40,20 Z"
          fill="hsl(var(--lilac-400))"
          opacity="0.7"
        />
      </svg>
      <div
        className="relative z-10 text-center max-w-[90%]"
        style={{ transform: `rotate(${-rotate}deg)` }}
      >
        {children}
      </div>
    </div>
  );
}
